import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from '../../redux/selectors/selectors';
import Portal from '../Portal';
import { useFloating, autoUpdate, offset, flip, useDismiss, useRole, useClick, useInteractions, shift } from '@floating-ui/react';

const isBlacklisted = (e, blacklist) => {
  if (blacklist) {
    for (let i = 0; i < blacklist.length; i++) {
      const b = blacklist[i];
      if (e?.target?.matches(b)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * @param props.active boolean
 * @param props.setActive function
 * @param props.className string
 * @param props.isEmpty boolean
 * @param props.padded boolean
 * @param props.children any
 * @param props.matchTriggerWidth boolean
 * @param props.alignWith string (dom element selector)
 * @param props.height string || number
 * @param props.width string || number
 * @param props.offset number
 * @param props.triggers array of strings (dom element selectors)
 * @param props.blacklist array of strings (dom element selectors)
 * @param props.parentId string (dom element id)
 * @param props.allowScroll boolean
 * @param props.placement string
 * @param props.strategy string
 * @param props.onHide function
 * @param props.crossAxisOffset number
 */
const FloatingPaneV2 = (props) => {
  const isMobile = useSelector(selectors.mobile);

  const [active, setActive] = useState(props.active === undefined ? false : props.active);
  const [trigger, setTrigger] = useState(undefined);
  const id = useRef(`floating-pane-${Math.floor(Math.random() * 100000)}`);

  const isActive = props.active !== undefined ? props.active : active;
  const allowScroll = props.allowScroll !== undefined ? props.allowScroll : true;
  const wrapperWidth = props.width || (props.matchTriggerWidth ? trigger?.offsetWidth : undefined);
  const wrapperHeight = props.height || 'auto';
  const alignElement = props.alignWith ? document.querySelector(props.alignWith) : undefined;

  const proxySetActive = (state) => {
    if (props.setActive !== undefined) {
      props.setActive(state);
      return;
    }
    setActive(state);
    if (!state) {
      props?.onHide?.();
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isActive,
    onOpenChange: proxySetActive,
    middleware: [offset({ mainAxis: props.offset || 0, crossAxis: props.crossAxisOffset || 0 }), flip(), shift()],
    placement: props.placement || 'bottom-start',
    strategy: props.strategy || 'fixed',
    whileElementsMounted: autoUpdate,
    elements: {
      reference: alignElement || trigger
    },
    transform: false
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: (e) => !e.target.closest('.workflow-input-variable-pane') });
  const role = useRole(context);

  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  useEffect(() => {
    const handleTriggerClick = (e) => {
      if (isBlacklisted(e, props.blacklist)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setTrigger(e.target);
      proxySetActive(true, e, 'handleTriggerClick');
    };

    const handleTriggerFocus = (e) => {
      if (isBlacklisted(e, props.blacklist)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setTrigger(e.target);
      proxySetActive(true, e, 'handleTriggerClick');
    };

    const matchTriggers = (element) => {
      if (!element || !element?.nodeName) {
        return false;
      }
      return props?.triggers?.some((selector) => {
        const elements = document.querySelectorAll(selector);
        // eslint-disable-next-line array-callback-return
        return Array.from(elements).some((el) => {
          if (typeof el?.contains === 'function') {
            return el.contains(element);
          }
        });
      });
    };

    const globalClickListener = (e) => {
      if (matchTriggers(e.target)) {
        handleTriggerClick(e);
      }
    };

    const globalFocusListener = (e) => {
      if (matchTriggers(e.target)) {
        handleTriggerFocus(e);
      }
    };

    window.addEventListener('click', globalClickListener);
    window.addEventListener('focus', globalFocusListener, true); // capture phase

    return () => {
      window.removeEventListener('click', globalClickListener);
      window.removeEventListener('focus', globalFocusListener, true); // capture phase
    };
  }, [props.triggers?.join(','), props.blacklist?.join(',')]);

  if (!isActive) {
    return null;
  }

  return (
    <Portal id={props.parentId || id.current}>
      <div className={`comp-floating-pane-v2 ${props.className || ''} ${allowScroll ? 'scroll' : 'no-scroll'}`}>
        {isMobile && <div className={`backing`} />}
        <div className={`pane ${props.isEmpty ? 'empty' : ''}`} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <div className={`pane-wrapper ${props?.padded ? 'padded' : ''}`} style={{ width: wrapperWidth, height: wrapperHeight }}>
            {props.children}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default FloatingPaneV2;
